<template>
<div class="sharing_status mb-4">
    <i v-if="loading" class="ion-load-c spin"></i>
    <template v-else-if="shares !== null">
        <div v-if="shares.length" class="share-grid">
            <share-card 
                v-for="share in shares" :key="share.id"
                :share="share" 
                :show_details="true" 
                @open="open_edit_share(share)"
                @revoke="revoke_share(share)"
                @delete="delete_share(share)"
            />
        </div>
        <div v-else class="text-success text-center">
            <i class="ion-locked mr-2"></i>This {{ is_folder ? 'folder' : 'file' }} is not shared
        </div>
    </template>
</div>
</template>
<script>

import { ShareService } from '@/services/share-service.js'
import { Utils } from '@/helpers/utils.js'

import shareCard from './share_card.vue'
import { init_shares } from '@/helpers/init-shares'
import { bus, events } from '@/helpers/event_bus'
import { NamespaceService } from '@/services/namespace-service'
import { useResponsive } from '@/helpers/useResponsive'

export default {

    props: {
        // The file or folder whose active shares are listed
        file: Object
    },

    components: { 
        shareCard 
    },

    data(){
        return {
            loading: false,
            shares: null
        }
    },

    computed: {
        team(){
            return this.user?.team || []
        },

        is_folder(){
            return this.file.entity_type == NamespaceService.ENTITY_TYPE_FOLDER
        },

        shares_num(){
            return this.shares?.length || 0
        },

        isMobile(){
            return useResponsive().isMobile
        }
    },

    watch: {
        shares_num(new_shares_num){
            this.$emit('shares_num', new_shares_num)
        }
    }, 

    mounted(){
        this.load_shares()

        bus.$on(events.SHARE_UPDATED, (updated_share) => {
            let local_share = this.shares?.find(s => s.id == updated_share.id)
            if(!local_share) return // a different share was updated

            this.on_share_updated(updated_share)
        })
    },

    methods: {

        async load_shares(){
            
            if(NamespaceService.ns_type !== NamespaceService.NS_TYPE_DEFAULT) return // not in Team Files, abort

            this.loading = true
            const shares = await ShareService.get_shares_of_file(this.file.id).then(res => res.body).catch(err => {
                console.error(err)
                Utils.show_error("Failed to load shares of " + this.file.name)
                return null
            }).finally(() => this.loading = false)
            if(shares === null) return
            
            await init_shares(shares)
            
            this.shares = [...shares]
            // report the number of shares even if it's zero (when the watcher is not triggered)
            this.$emit('shares_num', shares.length) 
        },

        async on_share_updated(updated_share){
            await init_shares([updated_share])
            const idx = this.shares.findIndex(s => s.id == updated_share.id)
            this.$set(this.shares, idx, updated_share)
        },

        revoke_share(share){
            ShareService.revoke_share(share.id).then(() => {
                Utils.show_success("Share disabled")
                this.on_share_removed(share)
            })
            .catch(err => {
                let str = "Error disabling share"
                if(err.message){ str += ": " + err.message }
                Utils.show_error(str)
            })
        },

        delete_share(share){
            ShareService.delete_share(share.id).then(() => {
                Utils.show_success("Share deleted")
                this.on_share_removed(share)
            })
            .catch(err => {
                let str = "Error deleting share"
                if(err.message){ str += ": " + err.message }
                Utils.show_error(str)
            })
        },

        on_share_removed(share){
            // Remove the share from this.shares
            this.shares.splice(this.shares.findIndex(s => s.id == share.id), 1)
        },

        open_edit_share(share){
            // Close the file info modal
            this.$emit('close')
            // Open the edit share modal
            this.$nextTick(() => {
                bus.$emit(events.EDIT_SHARE, structuredClone(share))
            })
        },

        to_clipboard(str){
            Utils.to_clipboard(str)
        }
    }

}
</script>
<style scoped>

    .share-header{
        border-bottom: 1px solid #ebebeb
    }
    .share-details{
        line-height: 2em
    }

    .centered-share-wrapper {
        margin: 0px auto;
        max-width: 400px;
    }

    .share-grid {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;     /* Center items horizontally */
        gap: 32px;                   /* Space between cards */
        align-items: flex-start;    /* Prevent vertical stretching */
    }

    .share-grid > * {
        flex: 1 1 350px;             /* Grow and shrink with a base width of 350px */
        max-width: 400px;            /* Limit stretching to 400px */
    }
</style>
